import React, { useState, useEffect, useRef } from "react";
import { MessageSquare, Send, Image as ImageIcon } from 'lucide-react';
import { supabase } from './lib/supabase';
import { useAuth } from './contexts/AuthContext';

function Chat() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const chatBoxRef = useRef(null);

  const fetchWikimediaImages = async (searchTerm) => {
    setLoadingImages(true);
    try {
      // Clean and prepare search term
      const keywords = searchTerm.split(' ').slice(0, 3).join(' ');
      
      // First, search for relevant pages
      const searchUrl = `https://commons.wikimedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(keywords)}&format=json&origin=*&srnamespace=6`; // Namespace 6 is for files
      
      const searchResponse = await fetch(searchUrl);
      const searchData = await searchResponse.json();
      
      if (!searchData.query?.search) {
        setImages([]);
        return;
      }

      // Get details for the first 4 images
      // const imagePromises = searchData.query.search.slice(0, 4).map(async (result) => {
        const imagePromises = searchData.query.search.slice(0, 2).map(async (result) => {
        const title = result.title;
        const imageInfoUrl = `https://commons.wikimedia.org/w/api.php?action=query&titles=${encodeURIComponent(title)}&prop=imageinfo&iiprop=url|extmetadata&format=json&origin=*`;
        
        const infoResponse = await fetch(imageInfoUrl);
        const infoData = await infoResponse.json();
        
        const pages = infoData.query?.pages || {};
        const page = Object.values(pages)[0];
        const imageInfo = page?.imageinfo?.[0];
        
        if (imageInfo) {
          return {
            url: imageInfo.url,
            alt: title.replace('File:', ''),
            credit: imageInfo.extmetadata?.Artist?.value || 'Wikimedia Commons',
            description: imageInfo.extmetadata?.ImageDescription?.value || ''
          };
        }
        return null;
      });

      const imageResults = (await Promise.all(imagePromises)).filter(img => img !== null);
      setImages(imageResults);
    } catch (error) {
      console.error('Error fetching Wikimedia images:', error);
      setImages([]);
    }
    setLoadingImages(false);
  };

  const saveChatMessage = async (message, aiResponse) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('chat_history')
        .insert({
          user_id: user.id,
          message: message,
          response: aiResponse
        });

      if (error) throw error;
    } catch (err) {
      console.error('Error saving chat message:', err);
    }
  };

  const sendQuery = async () => {
    if (!query.trim()) return;
    
    setLoading(true);
    setImages([]); // Clear previous images

    try {
      const res = await fetch("https://aitutorbackend-production.up.railway.app/chat/", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ query }),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }

      const data = await res.json();
      setResponse(data.ai_response);
      setChatHistory(prev => [...prev, { role: "user", content: query }, { role: "ai", content: data.ai_response }]);
      
      // Fetch relevant historical images from Wikimedia
      await fetchWikimediaImages(query);
      
      // Save chat message to Supabase
      await saveChatMessage(query, data.ai_response);
    } catch (error) {
      console.error("Error fetching AI response:", error);
      setResponse("⚠️ Sorry, I couldn't fetch a response. Please try again!");
    }

    setQuery("");
    setLoading(false);
  };

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTo({ top: chatBoxRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [chatHistory]);

  return (
    <div className="min-h-screen bg-[#f4e4bc] pt-16 font-serif">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <MessageSquare className="h-16 w-16 text-[#8b4513]" />
          </div>
          <h1 className="text-4xl font-extrabold text-[#8b4513] sm:text-5xl md:text-6xl"
              style={{ textShadow: '2px 2px 4px rgba(139, 69, 19, 0.2)' }}>
            Historical Conversations
          </h1>
          <p className="mt-3 max-w-md mx-auto text-base text-[#5c2e0a] sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
            Engage in enlightening discussions about history with our AI tutor
          </p>
        </div>

        <div className="flex gap-6">
          {/* Chat Section */}
          <div className="flex-1 bg-[#fffbf0] rounded-xl shadow-lg p-6 border-4 border-[#8b4513]"
               style={{ boxShadow: '8px 8px 0px rgba(139, 69, 19, 0.2)' }}>
            {/* Chat Box */}
            <div 
              ref={chatBoxRef} 
              className="h-[400px] overflow-y-auto mb-4 p-4 bg-[#fff8e7] rounded-lg border-2 border-[#8b4513]/20"
            >
              {chatHistory.length === 0 ? (
                <p className="text-[#8b4513]/60 text-center italic">Begin your historical inquiry...</p>
              ) : (
                chatHistory.map((chat, index) => (
                  <div 
                    key={index} 
                    className={`mb-4 flex ${chat.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`p-4 rounded-lg max-w-[75%] animate-fadeIn ${
                        chat.role === "user"
                          ? "bg-[#8b4513] text-white"
                          : "bg-white border-2 border-[#8b4513]/20"
                      }`}
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      {chat.content}
                    </div>
                  </div>
                ))
              )}
              
              {loading && (
                <div className="flex justify-center mt-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#8b4513] border-t-transparent"></div>
                </div>
              )}
            </div>

            {/* Input Area */}
            <div className="flex gap-2">
              <input
                type="text"
                className="flex-1 pl-4 pr-4 py-3 border-2 border-[#8b4513] rounded-lg bg-[#fff8e7] focus:ring-2 focus:ring-[#8b4513] focus:border-[#8b4513] font-serif placeholder-[#8b4513]/60"
                placeholder="Ask about any historical event..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && sendQuery()}
              />
              <button
                className="px-6 py-3 bg-[#8b4513] text-white rounded-lg hover:bg-[#6d3610] transition duration-200 flex items-center gap-2"
                onClick={sendQuery}
                disabled={loading}
                style={{ boxShadow: '3px 3px 0px rgba(139, 69, 19, 0.2)' }}
              >
                <Send className="h-4 w-4" />
                {loading ? "Thinking..." : "Send"}
              </button>
            </div>
          </div>

          {/* Images Section */}
          <div className="w-96 bg-[#fffbf0] rounded-xl shadow-lg p-6 border-4 border-[#8b4513] h-fit"
               style={{ boxShadow: '8px 8px 0px rgba(139, 69, 19, 0.2)' }}>
            <div className="flex items-center gap-2 mb-4">
              <ImageIcon className="h-5 w-5 text-[#8b4513]" />
              <h3 className="text-lg font-semibold text-[#8b4513]">Historical Images</h3>
            </div>
            
            {loadingImages ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#8b4513] border-t-transparent"></div>
              </div>
            ) : images.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
              {/* // <div className="grid grid-cols-1 gap-4"> */}

                {images.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={image.url}
                      alt={image.alt}
                      className="w-full h-32 object-cover rounded-lg border-2 border-[#8b4513]/20 transition-transform duration-200 hover:scale-105"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs p-2 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <p className="font-semibold mb-1">{image.alt}</p>
                      <p className="text-xs opacity-75">Source: {image.credit}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-[#8b4513]/60">
                <ImageIcon className="h-12 w-12 mb-2 opacity-50" />
                <p className="text-center">Ask a question to see relevant historical images</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Chat;